import React, { useEffect, useState } from 'react';
import { Button, Label, Radio } from 'flowbite-react';
import DriveCompanySelectionWithId from './DriveCompanySelectionWithId';
import { CompanyDocument, DriveRoundType, UserCareerPathType } from 'types';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import { useMutation, useQueryClient } from 'react-query';
import { getDrivePositionsOfACompany, getOfflineDrivePositions } from 'src/apis/offlineDrive';
import { useToast } from 'src/context/ToastProvider';
import Select from 'react-select';
import { formatCareerPathType, revertPositionTitleV2, stringAvatar } from 'shared-utils';
import { Avatar } from '@mui/material';
import { usePushUserInPositionMutation } from 'src/hooks/utils/mutations/offlineDriveMutations/usePushUserInPositionMutation.hooks';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { useGetPositionByIdQuery } from 'src/hooks/position.hooks';
import { useGetOfflineDrivePositions } from 'src/hooks/offlineDrive.hooks';

interface AssignCompanyFormProps {
	driveId: string;
	userIds: string[];
	onClose?: () => void;
	heading?: string;
	onFinish?: () => void;
	padding?: string;
}

const intialFormData = {
	company: '',
	companyName: '',
	position: '',
	positionName: '',
	selectedRound: '',
};

const AssignCompanyForm: React.FC<AssignCompanyFormProps> = ({
	driveId,
	userIds,
	onClose,
	onFinish,
	padding = 'p-4',
	heading = 'Assigning New Round',
}) => {
	const toast = useToast();
	const { positionId } = useParams();
	const queryClient = useQueryClient();
	const [avatarImg, setAvatarImg] = useState('');
	const [formData, setFormData] = useState(intialFormData);

	const generateAvatar = async ({ logo, url }: { logo: string; url: string }) => {
		const res = await generateCompanyLogoAvatarSrc(logo || '', url || '');
		setAvatarImg(res);
	};

	const getOfflineDrivePositionsQuery = useMutation(['getOfflineDrivePositions'], getOfflineDrivePositions, {
		onSuccess(data) {
			console.log('data?.positions:', data?.positions);
			handleFormDataChange({
				company: data?.positions[0]?.company?._id || '',
				companyName: data?.positions[0]?.company?.name || '',
				position: positionId || '',
				positionName: formatCareerPathType(data?.positions[0]?.title as UserCareerPathType),
				selectedRound: '',
			});
			getDrivePositionsOfACompanyMutation.mutate({
				companyId: data?.positions[0]?.company?._id as CompanyDocument['_id'],
				driveId,
			});
			generateAvatar({ logo: data?.positions[0]?.company?.logo || '', url: data?.positions[0]?.company?.url || '' });
		},
	});
	useEffect(() => {
		if (driveId && positionId) {
			getOfflineDrivePositionsQuery.mutate({
				driveId,
				positionId: positionId || '',
				page: 0,
				limit: 10,
			});
		}
	}, [driveId, positionId]);

	const getDrivePositionsOfACompanyMutation = useMutation(
		['getDrivePositionsOfACompany'],
		getDrivePositionsOfACompany,
		{
			onSuccess(data) {
				console.log(data);
			},
			onError(error: Error) {
				toast({
					title: `Error while performing this action: ${error.message}`,
					type: 'error',
				});
			},
		},
	);

	const handleFormDataChange = (newData: Partial<typeof intialFormData>) => {
		const updatedFormData = { ...formData, ...newData };
		setFormData(updatedFormData);
	};

	const pushUserInPositionMutation = usePushUserInPositionMutation({
		onComplete: () => {
			onClose && onClose();
			queryClient.invalidateQueries(['getOfflineDriveCandidates', driveId]);
			queryClient.refetchQueries(['getOfflineDriveCandidates', driveId]);

			toast({
				title: 'Round assigned successfully',
			});
			onFinish && onFinish();
		},
	});

	const resetForm = () => {
		setFormData(intialFormData);
		onClose && onClose();
	};

	const roundTypes = [
		{
			label: 'Assignment',
			value: DriveRoundType.ASSIGNMENT,
		},
		{
			label: 'Interview',
			value: DriveRoundType.INTERVIEW,
		},
		{
			label: 'Final Round',
			value: DriveRoundType.FINAL_ROUND,
		},
	];

	return (
		<div>
			{heading ? (
				<div className={classNames('py-4 border-b border-[#E5E7EB] mb-3', padding)}>
					<h6 className="text-[#1F3343] text-xl font-semibold leading-[28px]">{heading}</h6>
				</div>
			) : null}
			<div className={classNames('flex flex-col gap-5', padding)}>
				<div className="w-full">
					<div className="mb-2 block">
						<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="company" value="Company" />
						<span className="text-red-600"> *</span>
					</div>
					<DriveCompanySelectionWithId
						driveId={driveId}
						selectedCompany={formData.company ? { label: formData.companyName, value: formData.company } : undefined}
						onChange={data => {
							handleFormDataChange({
								company: data?.value as CompanyDocument['_id'],
								companyName: data?.label as CompanyDocument['name'],
								position: positionId || '',
								positionName: '',
								selectedRound: '',
							});
							getDrivePositionsOfACompanyMutation.mutate({
								companyId: data?.value as CompanyDocument['_id'],
								driveId,
							});
							generateAvatar({ logo: data?.logo || '', url: data?.url || '' });
						}}
					/>
				</div>

				{formData.company && (
					<div className="w-full">
						<div className="mb-2 block">
							<Label
								className={'dark:text-[#fff] text-[#111928] font-inter'}
								htmlFor="selectedRound"
								value={'Select Position'}
							/>
							<span className="italic text-xs text-gray-500">
								{formData.positionName
									? ` (${formatCareerPathType(formData.positionName as UserCareerPathType)}-${formData.position})`
									: ''}
							</span>
							<span className="text-red-600"> *</span>
						</div>

						<Select
							menuPosition="fixed"
							menuPlacement={'auto'}
							closeMenuOnSelect={true}
							blurInputOnSelect={true}
							classNamePrefix="flowbite-select"
							options={getDrivePositionsOfACompanyMutation?.data?.positions?.map((position: any) => ({
								label: `${formatCareerPathType(position.title as UserCareerPathType)}${position.positionLabel ? ` - (${position.positionLabel})` : ''
									} - ${position?.positionRequirement?.mustHaveskills?.map((skill: any) => skill.name).join(', ') || ''}`,
								value: position._id,
							}))}
							value={
								formData.position
									? {
										label: formData.positionName,
										value: formData.position,
									}
									: null
							}
							onChange={value => {
								handleFormDataChange({
									position: value?.value || '',
									positionName: value?.label || '',
								});
							}}
							placeholder="Select Position"
						/>
					</div>
				)}

				{formData.companyName && formData.positionName && (
					<>
						<div className="flex gap-2">
							<div className="flex justify-center items-center text-center min-w-[36px] min-h-[36px] max-w-[36px] max-h-[36px] rounded-full border border-[#F3F2F2]">
								<Avatar
									alt="companyLogo"
									src={avatarImg}
									sx={{
										width: '36px',
										height: '36px',
										minWidth: '36px',
										minHeight: '36px',
										fontSize: '12px',
									}}
								>
									{stringAvatar(formData.companyName.trim().toUpperCase() || '')}
								</Avatar>
							</div>
							<div className="flex flex-col gap-[2]">
								<p className="text-base font-medium text-[#1C1C28]">{formData.companyName}</p>
								<p className="text-xs text-[#28293D]">{formData.positionName}</p>
							</div>
						</div>

						<div className="w-full">
							<div className="mb-2 block">
								<Label
									className={'dark:text-[#fff] text-[#111928] font-inter'}
									htmlFor="selectedRound"
									value="Select Round"
								/>
								<span className="text-red-600"> *</span>
							</div>
							<div className="flex flex-col space-y-2">
								{roundTypes.map(roundType => (
									<label key={roundType.value} className="flex items-center">
										<Radio
											name="selectedRound"
											style={{ backgroundSize: '1.5rem' }}
											value={roundType.value}
											checked={formData.selectedRound === roundType.value}
											onChange={() => handleFormDataChange({ selectedRound: roundType.value })}
										/>
										<span className="ml-2">{roundType.label}</span>
									</label>
								))}
							</div>
						</div>
					</>
				)}
			</div>
			<div className={classNames('flex justify-end gap-2 py-3 border-t border-[#E5E7EB] mt-3', padding)}>
				{onClose ? (
					<Button color="light" type="button" onClick={resetForm}>
						Cancel
					</Button>
				) : null}
				<Button
					className={classNames(onClose ? '' : 'w-full')}
					onClick={() => {
						pushUserInPositionMutation.mutate({
							driveId,
							userIds,
							positionId: formData.position,
							roundType: formData.selectedRound,
						});
					}}
					color="purplePlaced"
					type="submit"
					disabled={
						!formData.company || !formData.position || !formData.selectedRound || pushUserInPositionMutation.isLoading
					}
				>
					Assign Candidate
				</Button>
			</div>
		</div>
	);
};

export default AssignCompanyForm;
